import { queryOptions, useQuery } from '@tanstack/react-query';
import { apiClient } from '../client';
import { recordingKeys } from '../query-keys';
import { useAuthStore } from '@/stores/auth-store';
import {
  showProcessStepRecordingContract,
  type ShowProcessStepRecordingResponse
} from '@bliks-io/contracts';

export const stepRecordingQueryOptions = (id: string) =>
  queryOptions({
    queryKey: recordingKeys.stepRecording(id),
    queryFn: async ({ signal }) => {
      const { data } = await apiClient.get<ShowProcessStepRecordingResponse>(
        showProcessStepRecordingContract.path.replace(':id', id),
        { signal }
      );
      return data;
    }
  });

/**
 * Fetch a single process step recording by ID.
 *
 * AI summary generation is now synchronous and driven by the review dialog
 * via `useGenerateStepRecordingSummary`, so no polling is needed here.
 *
 * @example
 * ```tsx
 * function StepRecordingPage({ recordingId }: { recordingId: string }) {
 *   const { data: recording, isLoading } = useProcessStepRecording(recordingId);
 *
 *   if (isLoading) return <Skeleton />;
 *   if (!recording) return <NotFound />;
 *
 *   return <RecordingView recording={recording} />;
 * }
 * ```
 */
export function useProcessStepRecording(id: string) {
  const { token } = useAuthStore();

  return useQuery({
    ...stepRecordingQueryOptions(id),
    enabled: !!token && !!id
  });
}
